/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import PackageDetail from '@paquery-team/lib-package-detail';
import { Button, notification } from 'antd';
import { useHistory, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import useMarketplaces from 'redux/marketplaces';
import { marketplacesItemsSelector } from 'redux/marketplaces/selectors';
import useStore from 'redux/store/index';
import { actions as storeActions } from 'redux/store/slice';
import { storeItemsSelector } from 'redux/store/selectors';
import { authProfile } from 'redux/auth/selectors';
import {
  countriesGlobalsSelector,
  selectGlobals,
} from 'redux/globals/selectors';
import { ColumnWidthOutlined, DownloadOutlined } from '@ant-design/icons';
import { getZoneFromAddress } from 'constants/operations';
import {
  getPackage as fetchPackageDetail,
  arrivePacketByExternalCode,
} from 'services/PackageService';
import { isPaqueryPointRole } from 'constants/roles';
import {
  createSegaShipment,
  downloadShipmentLabel,
  toSegaRow,
} from 'services/ShipmentSegaService';
import { downloadOrNotify } from 'components/massive/download';
import MeasuresTab from 'components/shipment/measuresTab';
import API from 'constants/api';
import { getData } from 'util/Api';
import { PACKAGE } from 'constants/packages';
import { useZones } from 'services/ZoneService';
import { useDeliveryTermOptions } from 'hooks/useOptions';

const validationSchema = Yup.object().shape({
  externalCode: Yup.string()
    .matches(/^\S+$/, 'El código de seguimiento no debe contener espacios.')
    .nullable(),
  caption: Yup.string()
    .nullable()
    .required('Especifique el contenido del paquete.'),
  estimatedCost: Yup.number().nullable(),
  detail: Yup.string().nullable(),
  packageSize: Yup.number().typeError(
    'Es necesario indicar el tamaño del paquete.',
  ),
  packageType: Yup.number().typeError('Es necesario elegir un tipo de envío'),
  deliveryTerm: Yup.number().when('packageType', {
    // ms-packages lo exige para enviar (2), retiro en tienda (4) y pickUp (3).
    // Flex (5) queda afuera: FlexPackageService lo pisa con SameDay.
    is: (packageType) =>
      packageType === 2 || packageType === 3 || packageType === 4,
    then: Yup.number().typeError('Es necesario elegir un plazo de entrega'),
    otherwise: Yup.number().nullable(),
  }),
  ownerID: Yup.number().typeError('Es necesario elegir un marketplace'),
  storeId: Yup.number().typeError('Es necesario elegir una tienda'),
  shippingScheduleOrigin: Yup.object().when('packageType', {
    // when package is type pickUp, name and address are not required
    is: 3,
    then: Yup.object().shape({
      name: Yup.string().nullable(),
      phone: Yup.number().typeError('Teléfono invalido').nullable(),
      shippingAddress: Yup.object().shape({
        addressDetail: Yup.string().nullable(),
      }),
    }),
    otherwise: Yup.object().shape({
      name: Yup.string()
        .nullable()
        .required('Especifique el nombre del punto de retiro.'),
      phone: Yup.number().typeError('Teléfono invalido').nullable(),
      shippingAddress: Yup.object().shape({
        addressDetail: Yup.string()
          .nullable()
          .required('Indique la dirección de retiro.'),
      }),
    }),
  }),
  shippingScheduleDestination: Yup.object().shape({
    name: Yup.string()
      .nullable()
      .required('Indique el nombre del destinatario.'),
    _countryDialingCode: Yup.string()
      .nullable()
      .required('El código de país es obligatorio'),
    phone: Yup.number()
      .transform((value, original) => (original === '' ? null : value))
      .nullable()
      .typeError('Teléfono invalido')
      .required('El teléfono de contacto es obligatorio'),
    destinationEmail: Yup.string()
      .nullable()
      .email('Ingrese un formato de correo válido.')
      .required('Ingrese una dirección de mail'),
    shippingAddress: Yup.object().shape({
      addressDetail: Yup.string()
        .nullable()
        .required('Indique la dirección de entrega'),
    }),
    deliveryNote: Yup.boolean().nullable(),
    paymentOnDelivery: Yup.boolean().nullable(),
    paymentAmount: Yup.number().when('paymentOnDelivery', {
      is: true,
      then: Yup.number()
        .required('Es necesario especificar un monto')
        .typeError('El monto debe ser un numero valido'),
      otherwise: Yup.number().nullable(),
    }),
  }),
  additionalCode: Yup.string().when('packageType', {
    // when package is type flex, additionalCode is required
    is: 5,
    then: Yup.string()
      .nullable()
      .required('Es necesario especificar el codigo de venta'),
    otherwise: Yup.string().nullable(),
  }),
  packageQuantity: Yup.number()
    .integer('La cantidad de bultos debe ser un número entero')
    .min(1, 'La cantidad de bultos no puede ser menor a 1')
    .nullable(),
  weight: Yup.number()
    .typeError('El peso debe ser un número')
    .moreThan(0, 'El peso tiene que ser mayor a cero')
    .nullable(),
  height: Yup.number().typeError('El alto debe ser un número').nullable(),
  width: Yup.number().typeError('El ancho debe ser un número').nullable(),
  length: Yup.number()
    .typeError('La profundidad debe ser un número')
    .nullable()
    .test(
      'dimensiones-completas',
      'Si cargás medidas, se necesitan 2 como mínimo',
      function validateDimensions(value) {
        const { height, width } = this.parent;
        const loaded = [height, width, value].filter(
          (dim) => dim !== undefined && dim !== null && dim !== '',
        );
        return loaded.length === 0 || loaded.length >= 2;
      },
    ),
});

const disabledInputs = {
  status: true,
  reason: true,
  shippingScheduleOrigin: {
    shippingAddress: {
      geoKey: true,
    },
  },
  shippingScheduleDestination: {
    scheduledDate: true,
    shippingAddress: {
      geoKey: true,
    },
  },
};

const initialValues = {
  externalCode: '',
  status: null,
  estimatedCost: '0',
  caption: '',
  reason: '',
  detail: '',
  ownerID: null,
  storeId: null,
  packageSize: null,
  packageType: null,
  deliveryTerm: null,
  additionalCode: '',
  packageQuantity: '1',
  weight: '',
  height: '',
  width: '',
  length: '',
  weightUnit: 'kg',
  lengthUnit: 'cm',
  shippingScheduleOrigin: {
    name: '',
    comment: '',
    phone: '',
    shippingAddress: {
      addressDetail: '',
      geoKey: '',
      comment: '',
    },
  },
  shippingScheduleDestination: {
    name: '',
    phone: '',
    destinationEmail: '',
    comment: '',
    zone: null,
    shippingAddress: {
      addressDetail: '',
      geoKey: '',
      comment: '',
    },
  },
};

const getPersonName = (fullName) => fullName.split(' ')[0];

const getPersonLastName = (fullName) => fullName.split(' ').slice(-1)[0];

const arriveCreatedPackage = async (externalCode) => {
  try {
    const response = await arrivePacketByExternalCode(externalCode);
    if (!response.data?.arrived) throw new Error(response.data?.message);
  } catch {
    notification.error({
      message: 'Oops!',
      description: `Ocurrió un error arribando el envío ${externalCode}.`,
    });
  }
};

const PackageSend = () => {
  useMarketplaces({ initialize: true });
  useStore();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const globals = useSelector(selectGlobals);
  const stores = useSelector(storeItemsSelector);
  const marketplaces = useSelector(marketplacesItemsSelector);
  const countries = useSelector(countriesGlobalsSelector);
  const {
    zones: { data: zones },
  } = useZones();
  const profileLogged = useSelector(authProfile);

  const [saving, setSaving] = useState(false);
  const [initialValuesDuplicate, setInitialValuesDuplicate] =
    useState(initialValues);
  // lib-package-detail precarga cada pestaña sólo al montarla, así que si los datos del
  // paquete a duplicar llegan después la pestaña ya montada queda vacía. Mostramos su
  // spinner hasta tener la respuesta y remontamos con `key` para que arranque cargado.
  // ponytail: el arreglo de fondo va en lib-package-detail, no en un hotfix.
  const [duplicateLoaded, setDuplicateLoaded] = useState(!location.state?.id);
  const modifiedStores = useRef(null);

  useEffect(() => {
    const getPackage = async (id) => {
      try {
        const response = await fetchPackageDetail(id);
        if (response.storeId) {
          const store = await getData(API.stores.getById(response.storeId));
          modifiedStores.current = [...stores, store];
        }
        setInitialValuesDuplicate({
          ...response,
          shippingScheduleDestination: {
            ...response.shippingScheduleDestination,
            zone: response.shippingScheduleDestination.zone?.id,
          },
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('error', err);
      } finally {
        setDuplicateLoaded(true);
      }
    };
    if (location.state?.id) {
      getPackage(location.state.id);
    }
  }, [location]);

  const submitEdition = async (values) => {
    setSaving(true);
    const source =
      values.packageType === PACKAGE.TYPES.PICKUP
        ? {
            ...values,
            shippingScheduleOrigin: values.shippingScheduleDestination,
          }
        : values;
    try {
      const resolvedZone = await getZoneFromAddress(
        source.shippingScheduleDestination.shippingAddress.addressDetail,
        profileLogged.logisticOperatorID,
      );
      if (!resolvedZone.id)
        notification.error({
          message: 'Oops!',
          description: 'No se ha encontrado una zona para esa dirección.',
        });

      // No catch block here, the PackageDetail component handles errors globally
      const result = await createSegaShipment(
        toSegaRow(source, resolvedZone),
        source.ownerID,
        source.storeId,
      );
      if (result.status === 'created') {
        notification.success({
          message: 'Envío creado',
          description: `Número de seguimiento ${result.tracking}`,
          btn: (
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() =>
                downloadOrNotify(
                  () =>
                    downloadShipmentLabel(
                      result.tracking,
                      'pdf',
                      source.ownerID,
                    ),
                  `${result.tracking}.pdf`,
                )
              }
            >
              Descargar etiqueta
            </Button>
          ),
        });
      } else {
        notification.info({
          message: 'El envío se está creando',
          description: 'Puede tardar unos segundos en aparecer en el listado.',
        });
      }
      setTimeout(() => {
        history.push('/packages');
      }, 1000);
    } finally {
      // Maybe change into onSubmit, onSuccess and onError callbacks in PackageDetail?
      setSaving(false);
    }
  };

  const selectMarketplace = (marketplaceId) => {
    dispatch(storeActions.all(marketplaceId));
  };

  const handleBlur = async (destinationAddressValue, setFieldValue) => {
    if (!destinationAddressValue) return;
    try {
      const zone = await getZoneFromAddress(
        destinationAddressValue,
        profileLogged.logisticOperatorID,
      );
      if (zone.id)
        setFieldValue(
          'shippingScheduleDestination.zone',
          parseInt(zone.id, 10),
        );
      else
        notification.error({
          message: 'Oops!',
          description: 'No se ha encontrado una zona para esa dirección.',
        });
    } catch (error) {
      notification.error({
        message: 'Oops!',
        description: 'Ha ocurrido un error. Intente de nuevo más tarde.',
      });
    }
  };

  const packageTypes = (globals.packages.type ?? []).filter(
    (type) => type.value !== PACKAGE.TYPES.RECEIVE,
  );
  const deliveryTerms = useDeliveryTermOptions(globals.deliveryTerm);

  const extraTabs = [
    {
      menuTitle: 'Peso y medidas',
      cardTitle: 'Peso y medidas',
      key: 'measures',
      icon: <ColumnWidthOutlined />,
      fieldListeners: ['weight', 'height', 'width', 'length'],
      content: <MeasuresTab />,
    },
  ];

  return (
    <PackageDetail
      key={duplicateLoaded ? 'ready' : 'loading'}
      extraTabs={extraTabs}
      packageTypes={packageTypes}
      packageSizes={globals.packages.size}
      deliveryTerms={deliveryTerms}
      marketplaces={marketplaces}
      stores={modifiedStores.current || stores}
      zones={zones}
      saving={saving}
      countries={countries}
      loaded={!!marketplaces && duplicateLoaded}
      disabledInputs={disabledInputs}
      initialValues={initialValuesDuplicate}
      validationSchema={validationSchema}
      onSubmit={submitEdition}
      onChangeMarketplace={selectMarketplace}
      onBlurDeliveryAddress={handleBlur}
    />
  );
};

export default PackageSend;
